import { Injectable, OnDestroy, Optional } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { environment } from '../../../environments/environment';
import { LabadminService } from '../../labadmin/labadmin.service';
import { UserSession } from './user.session';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';

const getLoggingConfig = () => (environment as any).logging;

export interface LogEntry {
  level: 'DEBUG' | 'INFO' | 'WARN' | 'ERROR';
  timestamp: string;
  userId: string;
  source: string;
  message: string;
  selectedTemplateName: string;
  deploymentKey: string;
  organizationId: string;
  accountId: string;
  url?: string;
  stackTrace?: string;
  metadata?: Record<string, any>;
  [key: string]: any;
}

/**
 * Unified Logger Service
 * 
 * Consolidates all logging functionality into a single service.
 * Provides structured logging with multiple log levels, batching, and automatic
 * transmission to backend API. All logging is fire-and-forget (non-blocking).
 * 
 * Features:
 * - Batched logging (sends logs in batches for efficiency)
 * - Stack trace extraction and line number parsing
 * - URL tracking
 * - Multiple log levels (DEBUG, INFO, WARN, ERROR)
 * - UI action tracking
 */
@Injectable({
  providedIn: 'root'
})
export class LoggerService implements OnDestroy {
  public baseUrl2 = environment.baseUrl2;
  private readonly isProduction: boolean = environment.production;
  
  // Store logs in buffer before sending
  private batchBuffer: LogEntry[] = [];
  private isSending = false;
  private flushTimeout: any = null;
  private readonly FLUSH_DELAY = 3000; // Send logs after 3 seconds if batch size not reached
  private readonly UI_ACTION_FLUSH_DELAY = 1000; // Send UI actions faster (1 second)
  private currentTemplateName: string | undefined;


  constructor(
    private httpClient: HttpClient,
    @Optional() private labadminService?: LabadminService,
    @Optional() private userSession?: UserSession,
    @Optional() private router?: Router,
    @Optional() private location?: Location
  ) {
    // No initialization needed - logs are sent when batch size is reached or after delay
  }



  /**
   * Set the current template name (menu/submenu name)
   * Components should call this method when navigating to a new template
   * @param templateName The name of the current template/menu
   */
  setTemplateName(templateName: string | undefined): void {
    this.currentTemplateName = templateName;
    // Also store in sessionStorage for persistence across component lifecycle
    if (templateName) {
      try {
        sessionStorage.setItem('CurrentSelectedTemplateName', templateName);
      } catch (error) {
        // Silently fail if sessionStorage is not available
      }
    } else {
      try {
        sessionStorage.removeItem('CurrentSelectedTemplateName');
      } catch (error) {
        // Silently fail if sessionStorage is not available
      }
    }
  }

  /**
   * Get current URL path
   */
  private getCurrentUrl(): string {
    try {
      if (this.location?.path()) {
        return this.location.path();
      }
      if (this.router?.url) {
        return this.router.url;
      }
      return window.location?.pathname || 'unknown';
    } catch (error) {
      return window.location?.pathname || 'unknown';
    }
  }

  /**
   * Get current call stack trace
   */
  private getCallStack(): string | undefined {
    try {
      const error = new Error();
      if (error.stack) {
        return error.stack;
      }
    } catch (e) {
      // Silently fail
    }
    return undefined;
  }

  /**
   * Get common metadata from LabadminService and other sources
   */
  private getCommonMetadata() {
    return {
      userId: this.labadminService?.labadminLoggedinUserEmail || this.userSession?.email_id || this.userSession?.user_id?.toString() || localStorage.getItem('UserEmail') || '',
      deploymentKey: this.labadminService?.deploymentKey || '',
      organizationId: this.labadminService?.organizationId ? String(this.labadminService.organizationId) : '',
      accountId: this.labadminService?.accountId ? String(this.labadminService.accountId) : '',
      selectedTemplateName: this.currentTemplateName || sessionStorage.getItem('CurrentSelectedTemplateName') || ''
    };
  }

  /**
   * Get logging API endpoint
   */
  private getLogApiEndpoint(): string {
    try {
      const baseUrl = this.labadminService?.baseUrl2 || this.baseUrl2;
      return baseUrl ? `${baseUrl}api/audit/clientlogs` : '';
    } catch (error) {
      return '';
    }
  }

  /**
   * Create log entry with required fields
   */
  private createLogEntry(
    level: LogEntry['level'], 
    source: string, 
    message: string, 
    metadata?: any,
    stackTrace?: string,
    error?: Error | any
  ): LogEntry {
    const common = this.getCommonMetadata();
    const getValue = (key: keyof typeof common) => metadata?.[key] || common[key] || '';
    
    // Get selected template name (check metadata first, then use common value)
    const selectedTemplateName = metadata?.templateName || metadata?.selectedTemplateName || getValue('selectedTemplateName');
    
    // Remove templateName from metadata if it exists (since it's now a top-level field)
    let finalMetadata = metadata;
    if (metadata && (metadata.templateName || metadata.selectedTemplateName)) {
      const { templateName, selectedTemplateName, ...rest } = metadata;
      finalMetadata = Object.keys(rest).length > 0 ? rest : undefined;
    }

    return {
      level,
      timestamp: new Date().toISOString(),
      source: source || 'UI_ACTION',
      message: message || '',
      userId: getValue('userId'),
      selectedTemplateName: selectedTemplateName,
      deploymentKey: getValue('deploymentKey'),
      organizationId: getValue('organizationId'),
      accountId: getValue('accountId'),
      url: this.getCurrentUrl(),
      stackTrace: stackTrace,
      metadata: finalMetadata
    };
  }

  /**
   * Send batch of logs to API endpoint as array
   */
  private sendBatchToApi(): void {
    if (this.isSending || this.batchBuffer.length === 0) {
      return;
    }

    // Clear any pending flush timeout since we're sending now
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
      this.flushTimeout = null;
    }

    this.isSending = true;
    const logsToSend = [...this.batchBuffer];
    this.batchBuffer = [];

    const endpoint = this.getLogApiEndpoint();
    
    if (!endpoint || endpoint.trim() === '') {
      if (!this.isProduction) {
        console.warn('LoggerService: No logging API endpoint configured.');
      }
      this.isSending = false;
      return;
    }

    // Transform logs to API format (array of objects)
    const apiPayloads = logsToSend.map(logEntry => ({
      level: logEntry.level,
      timestamp: logEntry.timestamp,
      userId: logEntry.userId || '',
      source: logEntry.source || 'UI_ACTION',
      message: logEntry.message || '',
      selectedTemplateName: logEntry.selectedTemplateName || '',
      deploymentKey: logEntry.deploymentKey || '',
      organizationId: logEntry.organizationId || '',
      accountId: logEntry.accountId || ''
    }));

    const dbName = this.labadminService?.deploymentKey || '';
    this.httpClient.post(endpoint, apiPayloads, { params: { 'Conn': dbName } }).pipe(
      catchError((error: HttpErrorResponse) => {
        // Put logs back if send fails
        this.batchBuffer.unshift(...logsToSend);
        this.isSending = false;
        console.warn('LoggerService: Failed to send logs:', error);
        return of(null);
      })
    ).subscribe({
      next: () => {
        this.isSending = false;
      },
      error: () => {
        this.isSending = false;
      }
    });
  }

  /**
   * Schedule a delayed flush if there are pending logs
   * @param isUIAction If true, uses shorter delay for UI actions
   */
  private scheduleDelayedFlush(isUIAction: boolean = false): void {
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
    }

    const delay = isUIAction ? this.UI_ACTION_FLUSH_DELAY : this.FLUSH_DELAY;

    this.flushTimeout = setTimeout(() => {
      if (this.batchBuffer.length > 0 && !this.isSending) {
        this.sendBatchToApi();
      }
      this.flushTimeout = null;
    }, delay);
  }

  /**
   * Core logging method - handles all log creation and buffering
   * @param isUIAction If true, uses shorter delay for UI actions
   */
  private log(level: LogEntry['level'], source: string, message: string, metadata?: any, stackTrace?: string, error?: Error | any, isUIAction: boolean = false): void {
    // Always capture logs - don't skip if config is missing (default to enabled)
    // Only skip if explicitly disabled
    const loggingConfig = getLoggingConfig();
    if (loggingConfig && loggingConfig.enableClientLogs === false) {
      return;
    }

    const entry = this.createLogEntry(level, source, message, metadata, stackTrace, error);
    
    this.batchBuffer.push(entry);
    
    // Schedule delayed flush - UI actions use shorter delay (1 second)
    this.scheduleDelayedFlush(isUIAction);
  }

  /**
   * Format error message from various error types
   */
  private formatErrorMessage(message: string, error?: any): string {
    if (!error) return message;
    if (error instanceof Error) {
      return `${message}: ${error.message}`;
    }
    return `${message}: ${JSON.stringify(error)}`;
  }

  // Public logging methods

  /**
   * Log a DEBUG message
   */
  debug(message: string, module: string, metadata?: Record<string, any>): void {
    if (!this.isProduction) {
      console.debug(`[${module}]`, message, metadata || '');
    }
    const stackTrace = this.getCallStack();
    this.log('DEBUG', module, message, metadata, stackTrace);
  }

  /**
   * Log an INFO message
   */
  info(message: string, module: string, metadata?: Record<string, any>): void {
    if (!this.isProduction) {
      console.info(`[${module}]`, message, metadata || '');
    }
    const stackTrace = this.getCallStack();
    this.log('INFO', module, message, metadata, stackTrace);
  }

  /**
   * Log a WARN message
   */
  warn(message: string, module: string, metadata?: Record<string, any>): void {
    if (!this.isProduction) {
      console.warn(`[${module}]`, message, metadata || '');
    }
    const stackTrace = this.getCallStack();
    this.log('WARN', module, message, metadata, stackTrace);
  }

  /**
   * Log an ERROR message
   */
  error(message: string, module: string, error?: Error | any, metadata?: Record<string, any>): void {
    let stackTrace: string | undefined;
    let errorMessage = message;

    if (error) {
      if (error instanceof Error) {
        stackTrace = error.stack;
        errorMessage = errorMessage || error.message;
      } else if (typeof error === 'string') {
        errorMessage = errorMessage || error;
      } else if (error?.message) {
        errorMessage = errorMessage || error.message;
        stackTrace = error.stack;
      }
    }

    if (!stackTrace) {
      stackTrace = this.getCallStack();
    }

    if (!this.isProduction) {
      console.error(`[${module}]`, errorMessage, error, metadata || '');
    }

    const errorMetadata = {
      ...metadata,
      error: error instanceof Error ? {
        message: error.message,
        stack: error.stack,
        name: error.name
      } : (typeof error === 'object' ? error : { error: String(error) })
    };

    this.log('ERROR', module, errorMessage, errorMetadata, stackTrace, error);
  }

  /**
   * Log UI action - tracks user interactions (button clicks, form submissions, etc.)
   * Automatically includes all common metadata (userId, organizationId, accountId, etc.)
   */
  logUIAction(message: string, metadata?: any): void {
    // Add to batch buffer for API transmission with faster flush for UI actions
    this.log('INFO', 'UI_ACTION', message, metadata, undefined, undefined, true);
  }

  /**
   * Log an HTTP error
   */
  logHttpError(
    url: string,
    statusCode: number,
    statusText: string,
    message: string,
    module: string = 'HttpInterceptor',
    metadata?: Record<string, any>
  ): void {
    const httpMetadata = {
      ...metadata,
      httpUrl: url,
      httpStatusCode: statusCode,
      httpStatusText: statusText
    };

    if (!this.isProduction) {
      console.error(`[${module}] HTTP ${statusCode} ${statusText}:`, url, message);
    }

    this.log('ERROR', module, `HTTP Error ${statusCode}: ${message}`, httpMetadata);
  }

  ngOnDestroy(): void {
    // Clear any pending flush timeout
    if (this.flushTimeout) {
      clearTimeout(this.flushTimeout);
      this.flushTimeout = null;
    }
    
    // Send remaining logs before cleanup
    if (this.batchBuffer.length > 0) {
      this.sendBatchToApi();
    }
  }
}
